<template>
  <header class="site-header">
    <div class="site-container site-header__inner">
      <router-link
        class="site-header__brand"
        :to="pathFor('home')"
        @click.native="closeMenu"
      >
        <img
          class="site-header__logo"
          :src="company.logo"
          :alt="company.name"
        />
        <div class="site-header__brand-copy">
          <span class="site-header__brand-kicker">{{ company.shortName }}</span>
          <strong>{{ company.name }}</strong>
        </div>
      </router-link>

      <nav class="site-nav" aria-label="Primary navigation">
        <router-link
          v-for="item in navItems"
          :key="item.key"
          class="site-nav__link"
          :class="{ active: isActive(item.key) }"
          :to="item.path"
        >
          {{ item.name }}
        </router-link>
      </nav>

      <div class="site-header__actions">
        <router-link
          class="button-brand site-header__cta"
          :to="pathFor('contact')"
        >
          {{ labels.businessContact }}
        </router-link>
        <router-link class="site-header__lang" :to="switchLanguagePath">
          {{ labels.switchLanguage }}
        </router-link>
        <button
          class="site-header__menu-button"
          type="button"
          :aria-label="labels.contactUs"
          @click="toggleMenu"
        >
          <img :src="company.menuIcon" alt="" />
        </button>
      </div>
    </div>

    <transition name="drawer-fade">
      <div v-if="navOpen" class="site-drawer" @click.self="closeMenu">
        <div class="site-drawer__panel">
          <div class="site-drawer__top">
            <div>
              <p class="site-drawer__eyebrow">{{ company.shortName }}</p>
              <strong class="site-drawer__title">{{ company.name }}</strong>
            </div>
            <button
              class="site-drawer__close"
              type="button"
              :aria-label="labels.contactUs"
              @click="closeMenu"
            >
              <img :src="company.closeIcon" alt="" />
            </button>
          </div>

          <div class="site-drawer__list">
            <router-link
              v-for="item in navItems"
              :key="item.key"
              class="site-drawer__link"
              :class="{ active: isActive(item.key) }"
              :to="item.path"
              @click.native="closeMenu"
            >
              {{ item.name }}
            </router-link>
          </div>

          <div class="site-drawer__actions">
            <router-link
              class="button-brand"
              :to="pathFor('contact')"
              @click.native="closeMenu"
            >
              {{ labels.businessContact }}
            </router-link>
            <router-link
              class="button-sand"
              :to="switchLanguagePath"
              @click.native="closeMenu"
            >
              {{ labels.switchLanguage }}
            </router-link>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import {
  getLangFromPath,
  getRoutePath,
  localizedSite
} from '@/content/localizedSite'

export default {
  name: 'Header',
  data() {
    return {
      navOpen: false
    }
  },
  computed: {
    lang() {
      return getLangFromPath(this.$route.path)
    },
    targetLang() {
      return this.lang === 'zh' ? 'en' : 'zh'
    },
    site() {
      return localizedSite[this.lang]
    },
    company() {
      return this.site.company
    },
    labels() {
      return this.site.labels
    },
    navItems() {
      return this.site.nav
    },
    currentKey() {
      return this.$route.meta.pageKey || 'home'
    },
    switchLanguagePath() {
      return getRoutePath(this.targetLang, this.currentKey)
    }
  },
  watch: {
    navOpen(value) {
      document.body.classList.toggle('menu-open', value)
    },
    $route() {
      this.closeMenu()
    }
  },
  beforeDestroy() {
    document.body.classList.remove('menu-open')
  },
  methods: {
    pathFor(key) {
      return getRoutePath(this.lang, key)
    },
    toggleMenu() {
      this.navOpen = !this.navOpen
    },
    closeMenu() {
      this.navOpen = false
    },
    isActive(key) {
      return this.currentKey === key
    }
  }
}
</script>

<style lang="scss" scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 40;
  padding: 9px 0;
  background: rgba(245, 244, 237, 0.9);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(224, 221, 207, 0.85);
}

.site-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.site-header__brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.site-header__logo {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: var(--color-ivory);
  box-shadow: var(--shadow-ring);
}

.site-header__brand-copy {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.site-header__brand-copy strong {
  max-width: 260px;
  overflow: hidden;
  color: var(--color-near-black);
  font-size: 0.95rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.site-header__brand-kicker {
  color: var(--color-coral);
  font-size: 12px;
  letter-spacing: 0;
}

.site-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}

.site-nav__link,
.site-drawer__link {
  transition: color 0.22s ease, background-color 0.22s ease;
}

.site-nav__link {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 999px;
  color: var(--color-charcoal);
  font-size: 15px;
}

.site-nav__link:hover,
.site-nav__link.active {
  background: rgba(201, 100, 66, 0.08);
  color: var(--color-near-black);
}

.site-nav__link::after {
  content: '';
  position: absolute;
  right: 14px;
  bottom: 7px;
  left: 14px;
  height: 1px;
  background: currentColor;
  opacity: 0;
  transform: scaleX(0);
  transform-origin: left;
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.site-nav__link:hover::after,
.site-nav__link.active::after {
  opacity: 0.42;
  transform: scaleX(1);
}

.site-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.site-header__cta {
  min-width: 104px;
}

.site-header__lang {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 999px;
  background: var(--color-warm-sand);
  color: var(--color-charcoal);
  font-size: 14px;
  box-shadow: var(--shadow-ring);
}

.site-header__menu-button {
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: var(--color-ivory);
  box-shadow: var(--shadow-ring);
  cursor: pointer;
}

.site-header__menu-button img {
  width: 22px;
  height: 22px;
}

.site-drawer {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  min-height: calc(100vh - 78px);
  padding: 12px 16px 24px;
  background: rgba(20, 20, 19, 0.22);
}

.site-drawer__panel {
  margin-left: auto;
  width: min(100%, 360px);
  min-height: calc(100vh - 114px);
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 22px;
  background: var(--color-ivory);
  border-radius: 24px;
  box-shadow: var(--shadow-whisper);
}

.site-drawer__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.site-drawer__eyebrow {
  margin: 0 0 8px;
  color: var(--color-coral);
  font-size: 12px;
}

.site-drawer__title {
  color: var(--color-near-black);
  line-height: 1.5;
}

.site-drawer__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: var(--color-warm-sand);
  cursor: pointer;
}

.site-drawer__close img {
  width: 18px;
  height: 18px;
}

.site-drawer__list,
.site-drawer__actions {
  display: grid;
  gap: 10px;
}

.site-drawer__link {
  display: block;
  padding: 12px 0;
  border-bottom: 1px solid var(--color-border-cream);
  color: var(--color-charcoal);
  font-size: 1.08rem;
  font-weight: 600;
}

.site-drawer__link.active {
  color: var(--color-terracotta);
}

.site-drawer__actions {
  margin-top: auto;
}

.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.22s ease;
}

.drawer-fade-enter,
.drawer-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: no-preference) {
  .site-header__logo,
  .site-header__lang,
  .site-header__menu-button {
    transition: transform 0.24s ease, box-shadow 0.24s ease;
  }

  .site-header__brand:hover .site-header__logo,
  .site-header__lang:hover,
  .site-header__menu-button:hover {
    transform: translateY(-2px);
  }
}

@media screen and (max-width: 992px) {
  .site-nav,
  .site-header__cta,
  .site-header__lang {
    display: none;
  }

  .site-header__menu-button {
    display: inline-flex;
  }
}

@media screen and (max-width: 640px) {
  .site-header__inner {
    gap: 14px;
  }

  .site-header__logo {
    width: 44px;
    height: 44px;
  }

  .site-header__brand {
    gap: 9px;
  }

  .site-header__brand-copy strong {
    max-width: 220px;
    font-size: 0.86rem;
  }
}
</style>
